import React, { useRef, useState } from "react";
import imageCompression from "browser-image-compression";
import { Upload, X, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { resolveImageUrl } from "../../core/utils/image";

interface ImageUploadProps {
    value?: string;
    onChange: (url: string) => void;
    label?: string;
    className?: string;
    maxSizeMB?: number;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
    value,
    onChange,
    label,
    className = "",
    maxSizeMB = 1,
}) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [preview, setPreview] = useState<string | null>(value || null);

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setUploading(true);
        setError(null);

        try {
            // 1. Compress and convert to WebP
            const options = {
                maxSizeMB,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                fileType: "image/webp",
            };

            const compressedFile = await imageCompression(file, options);
            const webpFile = new File([compressedFile], file.name.replace(/\.[^/.]+$/, "") + ".webp", {
                type: "image/webp",
            });

            // 2. Upload to backend
            const formData = new FormData();
            formData.append("file", webpFile);

            const response = await api.post<{ success: boolean; url: string }>("/api/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (response.data.success) {
                onChange(response.data.url);
                setPreview(response.data.url);
            } else {
                throw new Error("Upload failed");
            }
        } catch (err: any) {
            console.error("Upload error:", err);
            setError(err.message || t("uploadFailed", "Upload failed"));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onChange("");
        setPreview(null);
    };

    return (
        <div className={`space-y-2 ${className}`}>
            {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}

            <div
                onClick={() => fileInputRef.current?.click()}
                className={`
          relative border-2 border-dashed rounded-lg p-4 transition-all duration-200 cursor-pointer
          ${error ? "border-red-300 bg-red-50" : "border-gray-300 hover:border-accent hover:bg-gray-50"}
          ${preview ? "h-64" : "h-32"}
          flex flex-col items-center justify-center text-center
        `}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    className="hidden"
                />

                {uploading ? (
                    <div className="flex flex-col items-center text-accent">
                        <Loader2 className="w-8 h-8 animate-spin mb-2" />
                        <span className="text-sm">{t("uploading", "Compressing & Uploading...")}</span>
                    </div>
                ) : preview ? (
                    <div className="relative w-full h-full group">
                        <img
                            src={resolveImageUrl(preview)}
                            alt="Preview"
                            className="w-full h-full object-contain rounded-md"
                        />
                        <button
                            onClick={handleRemove}
                            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            title={t("remove", "Remove image")}
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col items-center text-gray-500">
                        <Upload className="w-8 h-8 mb-2" />
                        <span className="text-sm font-medium">{t("clickToUpload", "Click to upload")}</span>
                        <span className="text-xs text-gray-400 mt-1">
                            JPG, PNG, WebP (max {maxSizeMB}MB)
                        </span>
                    </div>
                )}
            </div>

            {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
    );
};
